import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BookOpen, Mail, Lock, Eye, EyeOff, Home, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Whitelist of same-origin paths we will honor from ?next=. Anything else
// (external URLs, protocol-relative, javascript:, etc.) falls back to '/'.
// This is the standard defense against open-redirect abuse when the login
// page is used as a hop in a phishing flow.
const safeNextPath = (raw) => {
  if (!raw) return '/';
  try {
    const decoded = decodeURIComponent(raw);
    if (!decoded.startsWith('/')) return '/';
    if (decoded.startsWith('//')) return '/';
    if (/^\/[a-z][a-z0-9+.-]*:/i.test(decoded)) return '/';
    return decoded;
  } catch {
    return '/';
  }
};

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  // Honor a ?next=/foo/bar querystring so callers like BookReader's
  // 401 handler can round-trip the reader back to the exact book.
  const nextTarget = safeNextPath(
    new URLSearchParams(location.search).get('next'),
  );

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: email,
        password: password
      });

      // Store token and user info
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('userName', response.data.name);
      localStorage.setItem('userRole', response.data.role);
      localStorage.setItem('role', response.data.role); // canonical key for ExpiryBanner
      localStorage.setItem('userId', response.data.user_id);
      localStorage.setItem('userEmail', email);
      // 2026-05 trial feature — persist expiry so ExpiryBanner can read it
      // without an extra API roundtrip. Stored as raw ISO string; null /
      // missing => lifetime access.
      if (response.data.valid_until) {
        localStorage.setItem('valid_until', response.data.valid_until);
      } else {
        localStorage.removeItem('valid_until');
      }
      if (response.data.avatar_id) {
        localStorage.setItem('userAvatarId', response.data.avatar_id);
        localStorage.setItem('avatar_id', response.data.avatar_id);
      } else {
        localStorage.removeItem('userAvatarId');
        localStorage.removeItem('avatar_id');
      }
      if (response.data.avatar_photo_url) {
        localStorage.setItem('userAvatarPhotoUrl', response.data.avatar_photo_url);
        localStorage.setItem('avatar_photo_url', response.data.avatar_photo_url);
      } else {
        localStorage.removeItem('userAvatarPhotoUrl');
        localStorage.removeItem('avatar_photo_url');
      }

      toast({
        title: 'Welcome back!',
        description: `You have successfully logged in as ${response.data.role === 'admin' ? 'Admin' : 'Member'}.`,
      });
      navigate(nextTarget);
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: 'Login Failed',
        description: error.response?.data?.detail || 'Incorrect email or password.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black relative overflow-hidden">
      {/* Home Button */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 z-20 flex items-center space-x-2 text-white/70 hover:text-white transition-colors bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg backdrop-blur-sm"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to Home</span>
      </button>

      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1920&q=80"
          alt="Books background"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/90 to-red-900/30" />
      </div>

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-black/80 backdrop-blur-lg p-8 rounded-lg shadow-2xl border border-white/10">
          {/* Logo */}
          <div className="flex items-center justify-center mb-8">
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL || ''}/api/static/scb-logo.png`}
              alt="Seven Chapter Books" 
              className="h-32 sm:h-40 w-auto cursor-pointer"
              style={{ filter: 'drop-shadow(0 4px 24px rgba(0, 0, 0, 0.85))', imageRendering: 'auto' }}
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              onClick={() => navigate('/')}
            />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white text-center mb-6">
            Sign In
          </h2>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600 focus:ring-red-600"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600 focus:ring-red-600"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-red-400 hover:text-red-300 transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </button>
            </div>

            <Button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-6 transition-all"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Info */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/20" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-black/80 text-gray-400">New here?</span>
            </div>
          </div>

          <p className="text-center text-gray-300 text-sm">
            Don&apos;t have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="text-fuchsia-300 hover:text-fuchsia-200 font-semibold underline underline-offset-2"
              data-testid="login-to-signup"
            >
              Create one
            </button>
          </p>
          <p className="text-center text-gray-500 text-xs mt-2">
            New accounts are reviewed by our admin team and activated within one business day.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;